import React, { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { ArrowPathIcon, XMarkIcon, SignalSlashIcon } from '@heroicons/react/24/outline';
import { Button } from '@monorepo/common/components/ui';

/**
 * Service-worker lifecycle UI and the offline indicator.
 *
 * The service worker is registered with `registerType: 'prompt'`, so a new
 * build does NOT take over the page on its own. That matters here: VideoStudio
 * and ContentEditor hold unsaved work in memory, and an automatic reload would
 * throw it away with no warning. The user is told an update exists and picks
 * the moment.
 *
 * Both banners sit above the mobile tab bar and clear the home indicator via
 * the bottom safe-area inset.
 */

const Banner = ({ children, tone = 'neutral' }) => (
  <div
    className="pointer-events-none fixed inset-x-0 z-[120] flex justify-center px-4"
    style={{ bottom: 'calc(var(--safe-bottom) + 76px)' }}
  >
    <div
      className={[
        'pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-panel',
        'border px-4 py-3 shadow-float',
        tone === 'warning'
          ? 'border-warning bg-warning-tint text-warning'
          : 'border-line bg-surface-overlay text-ink',
      ].join(' ')}
      role="status"
      aria-live="polite"
    >
      {children}
    </div>
  </div>
);

// How long to wait for the new worker to take control before reloading
// anyway. Short enough not to feel broken, long enough that the normal path
// (controllerchange, which is near-instant) wins the race.
const RELOAD_FALLBACK_MS = 3000;

const PwaPrompts = () => {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      // Registration failing must never be fatal — the app works fine without
      // a service worker, it just loses offline support.
      console.warn('Service worker registration failed:', error);
    },
  });

  /**
   * Apply the update and reload — reloading OURSELVES rather than trusting
   * the library to do it.
   *
   * `updateServiceWorker(reloadPage)` ignores its argument (the parameter is
   * literally `_reloadPage`). All it does is post SKIP_WAITING; the reload
   * comes from a `controlling` listener vite-plugin-pwa adds, which fires
   * `window.location.reload()` only `if (event.isUpdate)`.
   *
   * And workbox-window decides `isUpdate` from
   * `Boolean(navigator.serviceWorker.controller)` at REGISTER time. On the
   * visit that first installs the worker there is no controller yet, so that
   * flag stays false for the whole page session — and because `clientsClaim`
   * is on, the first worker takes control immediately, so an update arriving
   * later in that same session hits a dead button.
   *
   * Measured, before this: the new worker activated, the banner stayed on
   * screen, and the page never reloaded. On a RETURNING visit the same code
   * worked, which is what made it look intermittent.
   *
   * So: listen for the controller changing, reload on it, and fall back to
   * reloading anyway — a worker that never claims must not leave the only
   * button on the banner doing nothing.
   */
  const applyUpdate = () => {
    let reloaded = false;
    const reload = () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    };
    navigator.serviceWorker?.addEventListener('controllerchange', reload, { once: true });
    setTimeout(reload, RELOAD_FALLBACK_MS);
    updateServiceWorker(true);
  };

  // `navigator.onLine` is the browser's own guess and it is not authoritative
  // (it reports true on a captive-portal wifi), but it is right often enough
  // to be worth surfacing, and the API errors cover the rest.
  const [offline, setOffline] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  );

  useEffect(() => {
    const online = () => setOffline(false);
    const down = () => setOffline(true);
    window.addEventListener('online', online);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', down);
    };
  }, []);

  if (offline) {
    return (
      <Banner tone="warning">
        <SignalSlashIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-body-sm">
          You&rsquo;re offline. Published data won&rsquo;t refresh until you reconnect.
        </span>
      </Banner>
    );
  }

  if (needRefresh) {
    return (
      <Banner>
        <ArrowPathIcon className="h-5 w-5 shrink-0 text-ink-muted" aria-hidden="true" />
        <span className="flex-1 text-body-sm">A new version is available.</span>
        <Button
          onClick={applyUpdate}
          variant="primary" size="sm" className="shrink-0"
        >
          Reload
        </Button>
        <Button
          onClick={() => setNeedRefresh(false)}
          aria-label="Dismiss"
          variant="ghost" icon size="sm" className="shrink-0"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </Button>
      </Banner>
    );
  }

  return null;
};

export default PwaPrompts;
